import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

import { isDefined } from "@bps/common-utils";
import {
  Dialog,
  IconButton,
  IOverflowSetItemProps,
  Link,
  MessageBar,
  MessageBarType,
  Spinner,
  Stack,
  TooltipHost
} from "@bps/fluent-ui";
import { UserStorageKeys } from "@libs/gateways/user-experience/UserExperienceGateway.dtos.ts";
import { clinicalRecordMapKey } from "@stores/clinical/utils/clinical.utils.ts";
import { useStores } from "@stores/hooks/useStores.ts";

import { ClinicalRecordsBar } from "./ClinicalRecordsBar.tsx";
import { PatientPillOverflowButton } from "./PatientPillOverFlowButton.tsx";
import { QuickLink } from "./QuickLink.tsx";

interface WarningProps {
  header: string;
  body: string;
}

const toGenericWarningProps = ({ count }: { count: number }): WarningProps => ({
  header: count > 1 ? `${count} items in progress` : "Items in progress",
  body:
    count > 1
      ? "Finalise or discard the items to close"
      : "Finalise or discard the item to close"
});

export const QuickLinksBar = observer(() => {
  const { clinical, userExperience, correspondence, document } = useStores();

  const [warning, setWarning] = useState<WarningProps | undefined>(undefined);

  const openedConsults = clinical.ui.openedRecordIds
    .filter(x => x.encounterId)
    .map(x => {
      return clinical.multipleEncountersRecordsMap.get(
        clinicalRecordMapKey(x.patientId, x.encounterId)
      );
    })
    .filter(isDefined);

  const openedDocumentLinks = correspondence.openedDocumentTabs
    .map(x => correspondence.correspondenceMap.get(x.documentId))
    .filter(isDefined);

  const openedTemplateLinks = document.openedTemplateTabs
    .map(x => document.templateMap.get(x.documentId))
    .filter(isDefined);

  const activeTemplateTab = document.activeTemplateTab;

  const patientPillItems: IOverflowSetItemProps[] =
    clinical.ui.openedRecordIds.map(x => {
      const key = clinicalRecordMapKey(x.patientId, x.encounterId);
      const record = clinical.multipleEncountersRecordsMap.get(key);

      return {
        key: `${key}${x.encounterId === undefined ? "-view" : ""}`,
        patientId: x.patientId,
        type: "patient",
        active:
          clinical.ui.activeClinicalRecord?.id === x.patientId &&
          clinical.ui.activeClinicalRecord?.encounterId === x.encounterId,
        encounterId: x.encounterId,
        record
      };
    });

  const documentPillItems: IOverflowSetItemProps[] =
    correspondence.openedDocumentTabs.map(docTabMap => {
      return {
        key: docTabMap.documentId,
        type: "document",
        active:
          correspondence.activeDocumentTab?.documentId === docTabMap.documentId,
        patientId: docTabMap.patientId,
        encounterId: docTabMap.encounterId,
        documentTabStatus: docTabMap.documentTabStatus
      };
    });

  const templatePillItems: IOverflowSetItemProps[] =
    document.openedTemplateTabs.map(tempTabMap => {
      return {
        key: tempTabMap.documentId,
        type: "template",
        active: activeTemplateTab?.documentId === tempTabMap.documentId,
        documentTabStatus: tempTabMap.documentTabStatus,
        newTemplateId: tempTabMap.newDocumentId
      };
    });

  const pillItems = patientPillItems
    .concat(documentPillItems)
    .concat(templatePillItems);

  const handleDocumentClick = (
    id: string,
    patientId: string,
    encounterId: string
  ) => {
    if (correspondence.activeDocumentTab?.documentId !== id)
      correspondence.activeDocumentTab = {
        documentId: id,
        patientId,
        encounterId
      };
  };

  const handleTemplateClick = (documentId: string) => {
    if (activeTemplateTab?.documentId !== documentId)
      document.setActiveTemplateTab({
        documentId
      });
  };

  useEffect(() => {
    if (
      !userExperience.userStorageMap.get(UserStorageKeys.QuickLinksOnBottom)
    ) {
      userExperience.getUserStorage(UserStorageKeys.QuickLinksOnBottom);
    }
  }, [userExperience]);

  const handleCloseWarning = () => setWarning(undefined);

  const handleCloseAll = async (event: React.MouseEvent<any>) => {
    event.preventDefault();
    await Promise.all(
      clinical.ui.openedRecordIds.map(x =>
        clinical.getRecord(x.patientId, { encounterId: x.encounterId })
      )
    );

    await clinical.closeAllRecords();
    await correspondence.closeAllDocuments();

    openedTemplateLinks.forEach(link => {
      document.closeTemplate(link.id);
    });

    const encountersLeftOpenCount = clinical.ui.openedRecordIds.length;
    const documentsLeftOpenCount = openedDocumentLinks.length;
    const templatesLeftOpenCount = openedTemplateLinks.length;

    if (
      encountersLeftOpenCount ||
      documentsLeftOpenCount ||
      templatesLeftOpenCount
    ) {
      setWarning(
        toGenericWarningProps({
          count:
            encountersLeftOpenCount +
            documentsLeftOpenCount +
            templatesLeftOpenCount
        })
      );
      return;
    }
  };

  const onPositionClicked = async () => {
    const quickLinksOnBottom = await userExperience.getUserStorage(
      UserStorageKeys.QuickLinksOnBottom
    );

    userExperience.saveToUserStorage(
      UserStorageKeys.QuickLinksOnBottom,
      !quickLinksOnBottom
    );
  };

  const onRenderOverflowButton = (
    overflowItems: IOverflowSetItemProps[] | undefined
  ): JSX.Element => {
    return (
      <PatientPillOverflowButton
        items={overflowItems ?? []}
        onDocumentClick={handleDocumentClick}
        onTemplateClick={handleTemplateClick}
      />
    );
  };

  const saveInProgress = openedConsults.some(x => x.saving);

  return (
    <>
      {pillItems.length > 0 && (
        <Stack
          horizontal
          verticalAlign="center"
          horizontalAlign="space-between"
          tokens={{ childrenGap: 16 }}
          styles={(_, theme) => ({
            root: {
              padding: "0 16px",
              minHeight: 40,
              borderBottom: `1px solid ${theme.appointmentStatusesColors.completedSecondary}`
            }
          })}
        >
          <ClinicalRecordsBar
            items={pillItems}
            onRenderItem={item => (
              <QuickLink
                item={item}
                handleDocumentClick={handleDocumentClick}
                handleTemplateClick={handleTemplateClick}
              />
            )}
            onRenderOverflow={onRenderOverflowButton}
          />

          <Stack horizontal>
            {saveInProgress ? (
              <Spinner />
            ) : (
              <Link styles={{ root: { width: 70 } }} onClick={handleCloseAll}>
                Close all
              </Link>
            )}

            <TooltipHost
              content={
                userExperience.quickLinksOnBottom ? "Move up" : "Move down"
              }
            >
              <IconButton
                onClick={onPositionClicked}
                iconProps={{
                  iconName: userExperience.quickLinksOnBottom
                    ? "header"
                    : "footer"
                }}
              />
            </TooltipHost>
          </Stack>

          {warning && (
            <Dialog
              hidden={false}
              onDismiss={handleCloseWarning}
              dialogContentProps={{
                title: warning.header,
                showCloseButton: true
              }}
              maxWidth={600}
            >
              <MessageBar messageBarType={MessageBarType.warning}>
                {warning.body}
              </MessageBar>
            </Dialog>
          )}
        </Stack>
      )}
    </>
  );
});
